import React, { useState } from 'react';
import { Box, Stack, Skeleton, Typography, Collapse, useMediaQuery } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

import { MerchantAwareLink as Link } from '../../../../components/core/MerchantAwareLink';
import { RefreshButton } from '../../../Paylinks/components/RefreshButton';
import { Button } from '../../../../components/core';
import { cardOnFilePaths } from '../../../../util/constants';
import { useGetAllMITsQuery } from '../../../../store/modules/mit/api';
import { RecentTransaction } from './RecentTransaction';
import theme from '../../../../styles/themes/theme';

type Props = {
  merchantId: string;
};

const ListWrapper = ({
  children,
  refetch,
}: {
  children: React.ReactNode;
  refetch?: () => void;
}) => {
  const isRefreshIconOnly = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isRefreshIconOnly ? 0.5 : 2 }}>
          <Typography
            component="h2"
            fontWeight="semibold"
            sx={{
              fontSize: { xs: '14px', md: '16px', lg: '20px' },
              whiteSpace: 'nowrap',
            }}
          >
            Recently created transactions
          </Typography>
          <RefreshButton onClick={refetch} iconOnly={isRefreshIconOnly} sx={{ p: 0 }} />
        </Box>
        <Button
          text="View all"
          variant="text"
          endIcon={<ChevronRight />}
          sx={{ fontSize: 16, whiteSpace: 'nowrap' }}
          component={Link}
          to={cardOnFilePaths.VIEW_ALL}
        />
      </Box>
      {children}
    </>
  );
};

export const RecentTransactionsList = ({ merchantId }: Props) => {
  const {
    data: mitData,
    isFetching,
    refetch,
  } = useGetAllMITsQuery({
    merchantId,
    pageSize: 5,
    sortBy: 'createdAt',
    orderBy: 'desc',
  });
  const mits = mitData?.data ?? [];
  const [expandedMIT, setExpandedMIT] = useState<string | null>(null);

  if (isFetching) {
    return (
      <ListWrapper>
        <Stack spacing={2}>
          {new Array(5).fill(0).map((_, i) => (
            <Skeleton
              sx={{ borderRadius: '8px' }}
              variant="rectangular"
              height={115}
              data-testid="paylink-skeleton"
              // eslint-disable-next-line react/no-array-index-key
              key={i}
            />
          ))}
        </Stack>
      </ListWrapper>
    );
  }

  if (!mits.length) {
    return (
      <ListWrapper refetch={refetch}>
        <Typography
          variant="body1"
          sx={{
            color: 'grey.600',
            fontSize: '14px',
            pt: 1,
          }}
        >
          No recent transactions found
        </Typography>
      </ListWrapper>
    );
  }

  return (
    <ListWrapper refetch={refetch}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: expandedMIT ? 0 : 2,
          overflow: 'hidden',
          transition: 'gap 0.4s',
        }}
      >
        {mits.map((mit) => (
          <Collapse in={expandedMIT === mit.mitId || !expandedMIT} key={mit.mitId}>
            <RecentTransaction
              mit={mit}
              isExpanded={expandedMIT === mit.mitId}
              setIsExpanded={setExpandedMIT}
            />
          </Collapse>
        ))}
      </Box>
    </ListWrapper>
  );
};
